import React from 'react';

import { QuestionCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { TenantSettings } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import {
  TenantSettingFieldType,
  TenantSettingNumberUnits,
  TenantSettingsField,
} from '@recurrency/core-api-schema/dist/common/tenantSettingsTypes';
import { FormInstance } from 'antd';
import { colors } from 'theme/colors';

import { shouldDisplayField, shouldDisableField } from 'pages/admin/SettingsPage/utils';

import { Badge } from 'components/Badge';
import { CheckboxFormItem, InputFormItem, MonthDayFormItem, SelectFormItem } from 'components/FormItems';
import { Props as InputFormItemProps, TextAreaFormItem } from 'components/FormItems/InputFormItem';
import { ExternalLink } from 'components/Links';
import { Tooltip } from 'components/Tooltip';

import { useGlobalApp } from 'hooks/useGlobalApp';

import {
  validateNumberIsInteger,
  validateNumberMaximumInclusive,
  validateNumberMinimumInclusive,
} from 'utils/validation';

import { SettingsFieldNumberGrid } from '../../pages/admin/SettingsPage/SettingsFieldNumberGrid';
import { DisabledTenantSettingFieldDescription } from './DisabledTenantSettingFieldDescription';
import { HiddenTenantSettingFieldDescription } from './HiddenTenantSettingFieldDescription';

export function SettingsField({
  field,
  form,
  allowClear = false,
  localSettings,
}: {
  field: TenantSettingsField;
  form: FormInstance;
  allowClear?: boolean;
  localSettings?: Partial<TenantSettings>;
}) {
  const { activeTenant, activeUser, activeTenantSettings, pendingTenantSettings } = useGlobalApp();
  const name = field.key;

  if (!activeTenantSettings || !pendingTenantSettings) {
    return null;
  }

  /**
   * When the displayCondition() returns false, we show the field as read-only internally.
   */
  const showField = shouldDisplayField(field, activeTenantSettings, pendingTenantSettings);

  if (!showField && !activeUser.isRecurrencyInternalUser) {
    return null;
  }

  // Does this field Have a display condition that will hide it from the tenant
  const isHiddenFromTenant = field.displayCondition !== undefined && !showField && activeUser.isRecurrencyInternalUser;

  /**
   * When the disableCondition() returns true, we disable the field.
   */
  const isFieldDisabled = shouldDisableField(field, localSettings ?? {});

  const titleElement = (
    <div id={field.title.toLowerCase().replaceAll(' ', '-')}>
      {field.title}
      {field.helpCenterUrl && (
        <ExternalLink to={field.helpCenterUrl} style={{ marginLeft: '4px' }}>
          <QuestionCircleOutlined />
        </ExternalLink>
      )}
      {(field.internalOnly || isHiddenFromTenant) && (
        <Tooltip
          className={css`
            margin-left: 4px;
          `}
          title="Only visible to Recurrency team"
        >
          <Badge variant="danger" label="Internal" />
        </Tooltip>
      )}
    </div>
  );
  const descriptionElement = (
    <div
      className={css`
        color: ${colors.neutral[500]};
        font-weight: normal;
      `}
    >
      {' '}
      {field.description}
    </div>
  );
  const labelElement = (
    <div>
      {titleElement}
      {isHiddenFromTenant && <HiddenTenantSettingFieldDescription settingField={field} />}
      {isFieldDisabled && <DisabledTenantSettingFieldDescription settingField={field} />}
      {descriptionElement}
    </div>
  );

  switch (field.type) {
    case TenantSettingFieldType.Boolean:
      return (
        <div>
          {/* Separating out the title so it's not clickable */}
          <div className="ant-form-item-label">{titleElement}</div>
          {isHiddenFromTenant && (
            <div>
              <HiddenTenantSettingFieldDescription settingField={field} />
            </div>
          )}
          {isFieldDisabled && (
            <div>
              <DisabledTenantSettingFieldDescription settingField={field} />
            </div>
          )}
          <CheckboxFormItem
            name={name}
            checkboxLabel={descriptionElement}
            className={css`
              margin-bottom: 28px;
              > div > div {
                // Prevent one line checkbox labels from having more padding than multi line ones
                min-height: 0;
              }
            `}
            disabled={isHiddenFromTenant || isFieldDisabled}
          />
        </div>
      );
    case TenantSettingFieldType.Select: {
      const filteredOptions = field.options.filter(
        (option) =>
          (!option.erpTypes || option.erpTypes.includes(activeTenant.erpType)) &&
          // Hide internal only setting values unless the user is internal
          (!option.internalOnly || activeUser.isRecurrencyInternalUser),
      );
      return (
        <SelectFormItem
          name={name}
          label={labelElement}
          allowClear={allowClear}
          placeholder="Select an option"
          options={filteredOptions.map((option) => ({
            value: option.value,
            label: option.label ?? option.value,
          }))}
          rules={[{ type: 'enum', enum: filteredOptions.map((option) => option.value) }]}
          disabled={isHiddenFromTenant || isFieldDisabled}
        />
      );
    }
    case TenantSettingFieldType.String:
      return <InputFormItem name={name} label={labelElement} allowClear={allowClear} />;
    case TenantSettingFieldType.Textarea:
      return <TextAreaFormItem name={name} label={labelElement} allowClear={allowClear} />;
    case TenantSettingFieldType.Number: {
      const additionalProps = field.units ? NumberUnitsConfiguration[field.units] : {};
      return (
        <InputFormItem
          name={name}
          label={labelElement}
          allowClear={allowClear}
          type="number"
          min={field.min}
          max={field.max}
          rules={[
            { validator: (rule, value) => validateNumberMinimumInclusive(rule, value, field.min) },
            { validator: (rule, value) => validateNumberMaximumInclusive(rule, value, field.max) },
            {
              validator: (rule, value) =>
                field.integerOnly ? validateNumberIsInteger(rule, value) : Promise.resolve(),
            },
          ]}
          step={field.step ?? 0.1}
          readOnly={isHiddenFromTenant || isFieldDisabled}
          {...additionalProps}
        />
      );
    }
    case TenantSettingFieldType.MonthDay:
      return (
        <MonthDayFormItem
          name={name}
          label={labelElement}
          allowClear={allowClear}
          disabled={isHiddenFromTenant || isFieldDisabled}
        />
      );
    case TenantSettingFieldType.NumberGrid:
      return (
        <div>
          {labelElement}
          <SettingsFieldNumberGrid field={field} form={form} readOnly={isHiddenFromTenant || isFieldDisabled} />
        </div>
      );
    default:
      return <div>Field not rendered because it is an unrecognized type</div>;
  }
}

const NumberUnitsConfiguration: Record<TenantSettingNumberUnits, Partial<InputFormItemProps>> = {
  [TenantSettingNumberUnits.Dollars]: {
    prefix: '$',
    step: 0.01,
  },
  [TenantSettingNumberUnits.Percent]: {
    suffix: '%',
    step: 1,
  },
};
